<template>
    <div class="email-template-editor">
        <div class="toolbar" v-if="!previewMode">
            <div class="left-buttons">
                <SupsisButton @click="$emit('close')" primary small> &lt; {{ $t("message.goBack") }}</SupsisButton>
                <SupsisButton @click="handleUpdateTemplate" primary small>
                    <i class="fas fa-save"></i> {{ $t("message.update") }}
                </SupsisButton>
            </div>
            <div class="right-buttons">
                <SupsisButton @click="handleMjmlModalOpen">MJML</SupsisButton>
                <SupsisButton @click="handleHtmlModalOpen">HTML {{ $t("message.show") }}</SupsisButton>
                <SupsisButton @click="handleModelModalOpen">Model {{ $t("message.show") }}</SupsisButton>
                <TemplateSelector @template-select="handleTemplateSelect" />
            </div>
        </div>

        <div ref="editor" class="grape-wrapper"></div>

        <template v-if="!previewMode">
            <Modal2 v-if="mjmlModalOpen" title="MJML" @cancel="mjmlModalOpen = false">
                {{ editor && editor.getHtml() }}
            </Modal2>

            <Modal2 v-if="htmlModalOpen" title="HTML" @cancel="htmlModalOpen = false">
                {{ getRawHtml() }}
            </Modal2>

            <Modal2 v-if="modelModalOpen" title="Model" @cancel="modelModalOpen = false">
                {{ editor && JSON.stringify(editor.getComponents()) }}
            </Modal2>
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import grapesjs from "grapesjs";
import gjsPresetNewsletter from "grapesjs-preset-newsletter";
import "grapesjs/dist/css/grapes.min.css";
import gjsMjml from "grapesjs-mjml";
import mjml2html from "mjml-browser";
import TemplateSelector from "./TemplateSelector.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { MAIL_API } from "../../../../api/mail";
import { uploadAsset } from "../../../../api/assetUpload";
import axios from "axios";
import { mapState } from "vuex";

export default Vue.extend({
    name: "EmailTemplateEditor",
    components: {
        TemplateSelector,
        Modal2,
        SupsisButton,
    },
    props: {
        template: {
            type: Object,
            required: true,
        },
        previewMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editor: null,
            mjmlModalOpen: false,
            htmlModalOpen: false,
            modelModalOpen: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
    },
    mounted() {
        let design = this.template.design;
        if (typeof design === "string") {
            design = JSON.parse(design);
        }

        const config = {
            container: this.$refs.editor,
            plugins: [gjsPresetNewsletter, gjsMjml],
            pluginsOpts: {
                gjsPresetNewsletter: {},
                gjsMjml: {},
            },
            components: design,
            style: `
                .mj-text {
                    padding: 20px;
                }
            `,
            storageManager: false,
            assetManager: {
                upload: false,
                uploadFile: false,
                multiUpload: false,
                autoAdd: false,
            },
        }
        if (this.previewMode) {
            config.canvas = {};
            config.commands = {};
            config.panels = {};
            config.deviceManager = {};
            config.selectorManager = {};
            config.editable = false;
            config.noticeOnUnload = false;
            config.showOffsets = false;
            config.showRulers = false;
            config.allowScripts = false;
            config.canvas.styles = [];
            config.canvas.scripts = [];
            config.commands.defaults = [];
            config.panels.defaults = [];
            config.deviceManager.devices = [];
            config.selectorManager.componentFirst = false;
        }
        const editor = grapesjs.init(config);

        this.editor = editor;

        // Asset upload için custom handler
        if (!this.previewMode) {
            this.setupAssetUpload(editor);
        }

        if (this.previewMode) {
            editor.getModel().set('draftMode', false);
            editor.Commands.stop('preview');
            editor.getModel().set('hoverable', false);
            editor.getModel().set('selectable', false);
            editor.getModel().set('draggable', false);
            editor.getModel().set('droppable', false);
            editor.getModel().set('highlightable', false);
            editor.refresh();

            editor.UndoManager.clear();
            editor.Panels.getPanels().reset();
            editor.getContainer().classList.add('gjs-preview-mode');
        }

        window.editor = editor;
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    },
    methods: {
        /**
         * Asset Manager için S3 upload yapılandırması
         * @param {Object} editor - GrapesJS editor instance
         */
        setupAssetUpload(editor) {
            const vm = this;

            // Asset Manager'ın açılmasını izle - birden fazla event dinle
            const setupUI = () => {
                setTimeout(() => {
                    const am = editor.AssetManager;
                    const container = am.getContainer();

                    if (!container) return;

                    // Default upload alanını gizle
                    const defaultUploader = container.querySelector('.gjs-am-file-uploader');
                    if (defaultUploader) {
                        defaultUploader.style.display = 'none';
                    }

                    // Custom upload UI'ı ekle (eğer yoksa)
                    if (!container.querySelector('.custom-asset-upload')) {
                        vm.addCustomUploadUI(container);
                    }
                }, 150);
            };

            // Birden fazla event dinle
            editor.on('run:open-assets', setupUI);
            editor.on('asset:open', setupUI);

            // Modal açılma eventini de dinle
            editor.on('modal:open', setupUI);
        },

        /**
         * Custom upload UI'ı ekle
         * @param {HTMLElement} container - Asset Manager container
         */
        addCustomUploadUI(container) {
            const uniqueId = `gjs-asset-file-input-${Date.now()}`;
            const uploadDiv = document.createElement('div');
            uploadDiv.className = 'custom-asset-upload';
            uploadDiv.innerHTML = `
                <div class="asset-upload-area">
                    <input type="file" id="${uniqueId}" accept="image/*" multiple style="display: none;" />
                    <label for="${uniqueId}" class="asset-upload-label">
                        <i class="fa fa-cloud-upload"></i>
                        <p>Upload Asset</p>
                        <span>Click or drag images here</span>
                    </label>
                </div>
            `;

            // Container'a direkt prepend et (en üste ekle)
            if (container.firstChild) {
                container.insertBefore(uploadDiv, container.firstChild);
            } else {
                container.appendChild(uploadDiv);
            }

            // File input event listener
            const fileInput = uploadDiv.querySelector('input[type="file"]');

            fileInput.addEventListener('change', async (e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                    await this.uploadFiles(files);
                    e.target.value = ''; // Reset input
                }
            });

            // Drag & drop desteği
            const uploadArea = uploadDiv.querySelector('.asset-upload-area');

            uploadArea.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadArea.classList.add('dragover');
            });

            uploadArea.addEventListener('dragleave', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadArea.classList.remove('dragover');
            });

            uploadArea.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadArea.classList.remove('dragover');

                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                    await this.uploadFiles(files);
                }
            });
        },

        /**
         * Dosyaları S3'e yükle
         * @param {FileList} files - Yüklenecek dosyalar
         */
        async uploadFiles(files) {
            const am = this.editor.AssetManager;

            if (!this.site || !this.site._id) {
                this.$message.error('Site information not found');
                return;
            }

            let uploadedCount = 0;

            try {
                this.$message.loading('Uploading assets...', 0);

                // Her dosyayı ayrı ayrı yükle
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];

                    // File type kontrolü
                    if (!file.type.startsWith('image/')) {
                        this.$message.warning(`${file.name} is not an image file`);
                        continue;
                    }

                    const path = `${this.site._id}/email-assets/`;

                    try {
                        // 1. Upload metadata gönder, signed URL al
                        const uploadData = await uploadAsset({
                            path,
                            name: file.name,
                            size: file.size,
                            type: 'mail',
                            mime: file.type,
                            ext: file.name.split('.').pop(),
                        });

                        if (!uploadData || !uploadData.data || !uploadData.data.asset) {
                            throw new Error('Invalid upload response');
                        }

                        // 2. Dosyayı S3'e yükle
                        await axios({
                            method: uploadData.data.method,
                            url: uploadData.data.url,
                            data: file,
                            headers: { 'Content-Type': 'application/octet-stream' },
                        });

                        // 3. Final URL'i oluştur
                        const asset = uploadData.data.asset;
                        const assetUrl = asset.v2Format && asset.cdnUrl
                            ? asset.cdnUrl
                            : `https://static.supsis.live/${path}${asset._id}`;

                        uploadedCount++;

                        // 4. Asset Manager'a ekle
                        am.add({
                            src: assetUrl,
                            type: 'image',
                            name: file.name,
                        });
                    } catch (error) {
                        console.error(`Failed to upload ${file.name}:`, error);
                        this.$message.error(`Failed to upload ${file.name}`);
                    }
                }

                this.$message.destroy();

                if (uploadedCount > 0) {
                    this.$message.success(`${uploadedCount} asset(s) uploaded successfully`);
                } else if (files.length > 0) {
                    this.$message.warning('No assets were uploaded');
                }
            } catch (error) {
                this.$message.destroy();
                console.error('Asset upload error:', error);
                this.$message.error('Failed to upload assets');
            }
        },

        handleMjmlModalOpen() {
            this.mjmlModalOpen = true;
        },
        handleHtmlModalOpen() {
            this.htmlModalOpen = true;
        },
        handleModelModalOpen() {
            this.modelModalOpen = true;
        },
        handleTemplateSelect(template) {
            this.editor.setComponents(template);
        },
        getRawHtml() {
            try {
                return mjml2html(this.editor?.getHtml())?.html;
            } catch (error) {
                console.error(error);
                return "";
            }
        },
        handleUpdateTemplate() {
            const updatedHtml = this.getRawHtml();
            const updatedDesign = this.editor.getComponents();

            const updatingFields = {
                html: updatedHtml,
                design: updatedDesign,
            };

            MAIL_API.TEMPLATE.updateEmailTemplate(this.template._id, updatingFields)
                .then((updatedTemplate) => {
                    this.$message.success(this.$t("mail.templateUpdateSucs"));
                    this.$emit("template-updated", updatedTemplate);
                })
                .catch((error) => {
                    console.error("Şablon güncellenirken bir hata oluştu:", error);
                    this.$message.error(this.$t("mail.templateUpdateError"));
                });
        },
    },
});
</script>

<style lang="scss">
.email-template-editor {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .grape-wrapper {
        height: calc(100vh - 156px) !important;
        max-height: calc(100vh - 156px) !important;

        &.preview-mode {
            height: 100vh !important;
            max-height: 100vh !important;
        }
    }

    .toolbar {
        margin-bottom: 10px;
        display: flex;
        justify-content: space-between;
        gap: 10px;

        .left-buttons,
        .right-buttons {
            display: flex;
            gap: 10px;
        }
    }

    iframe {
        height: 100% !important;
    }

    &:deep(.gjs-cv-canvas) {
        width: 100%;
        height: 100%;
        top: 0;
    }

    &:deep(.gjs-pn-panel) {
        padding: 0;
    }

    .gjs-preview-mode {

        .gjs-pn-panels,
        .gjs-dev-name,
        .gjs-frame-wrapper--no-select,
        .gjs-badges,
        .gjs-toolbar,
        .gjs-tools,
        .gjs-resizer,
        .gjs-highlighter,
        .gjs-offset-viewer,
        .gjs-selected {
            display: none !important;
        }

        .gjs-cv-canvas {
            width: 100% !important;
            height: 100% !important;
            left: 0 !important;
            top: 0 !important;
            padding: 0 !important;
        }

        .gjs-frame {
            border: none !important;
        }

        .gjs-cv-canvas-bg {
            width: 100% !important;
            height: 100% !important;
            top: 0 !important;
        }
    }

    // GrapesJS Asset Manager Özelleştirmeleri - Default UI'ları gizle
    &:deep(.gjs-am-file-uploader) {
        display: none !important;
    }

    &:deep(.gjs-am-add-asset) {
        display: none !important;
    }

    .gjs-asset-manager {
        display: flex !important;
        width: 100% !important;
    }

    // Asset Manager Modal Customize
    &:deep(.gjs-mdl-dialog) {
        background: #22272d !important;
        box-shadow: 0 0 30px rgba(0, 0, 0, 0.5) !important;

        .gjs-mdl-title {
            background: #1a1f24 !important;
            color: #fff !important;
            border-bottom: 1px solid #2d3339 !important;
            font-weight: 600 !important;
        }

        .gjs-mdl-content {
            background: #22272d !important;
            color: #e0e0e0 !important;
        }

        .gjs-am-assets-header {
            display: none !important; // URL ile manuel ekleme alanını gizle
        }

        .gjs-am-assets-cont {
            background: #22272d !important;
            padding-top: 0 !important;
        }

        .gjs-am-assets {
            background: #22272d !important;
            padding: 10px !important;
        }

        .gjs-am-asset {
            background: #2d3339 !important;
            border: 2px solid #3a4149 !important;
            border-radius: 8px !important;
            transition: all 0.3s ease !important;

            &:hover {
                border-color: #4a5560 !important;
                transform: translateY(-2px) !important;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
            }

            .gjs-am-preview {
                background: #1a1f24 !important;
            }

            .gjs-am-meta {
                background: #2d3339 !important;
                color: #e0e0e0 !important;
                border-top: 1px solid #3a4149 !important;
            }

            .gjs-am-name {
                color: #e0e0e0 !important;
            }

            .gjs-am-close {
                background: #e74c3c !important;
                color: #fff !important;
                border-radius: 50% !important;
                width: 24px !important;
                height: 24px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;

                &:hover {
                    background: #c0392b !important;
                }
            }
        }
    }

    // Modal backdrop
    &:deep(.gjs-mdl-dialog-bg) {
        background: rgba(34, 39, 45, 0.85) !important;
    }
}

// Custom Asset Upload UI - Global styles
.custom-asset-upload {
    padding: 15px;
    margin: 0;
    width: 60%;
    border-bottom: 2px solid #2d3339;

    .asset-upload-area {
        border: 2px dashed #4a5560;
        border-radius: 8px;
        padding: 35px 25px;
        text-align: center;
        background: linear-gradient(135deg, #2d3339 0%, #22272d 100%);
        transition: all 0.3s ease;
        cursor: pointer;
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(74, 85, 96, 0.2), transparent);
            transition: left 0.5s;
        }

        &:hover {
            border-color: #5a6570;
            background: linear-gradient(135deg, #353b42 0%, #2a3038 100%);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            transform: translateY(-2px);

            &::before {
                left: 100%;
            }

            i {
                transform: scale(1.1);
            }
        }

        &.dragover {
            border-color: #6a7580;
            border-style: solid;
            background: linear-gradient(135deg, #3a4149 0%, #2f353d 100%);
            box-shadow: 0 0 20px rgba(106, 117, 128, 0.4);

            i {
                animation: bounce 0.6s ease infinite;
            }
        }

        .asset-upload-label {
            cursor: pointer;
            display: block;
            margin: 0;
            position: relative;
            z-index: 1;

            i {
                font-size: 42px;
                color: #8a9299;
                display: block;
                margin-bottom: 14px;
                transition: all 0.3s ease;
            }

            p {
                font-size: 16px;
                font-weight: 600;
                color: #e0e0e0;
                margin: 0 0 8px 0;
                letter-spacing: 0.3px;
            }

            span {
                font-size: 13px;
                color: #8a9299;
                display: block;
                font-weight: 500;
            }
        }
    }
}

@keyframes bounce {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}
</style>
